import {
  FaHome,
  FaChartBar,
  FaDollarSign,
  FaListOl,
  FaAddressBook,
  FaUserPlus,
  FaUserCheck,
  FaShoppingCart,
  FaCalendar,
  FaCog,
  FaSitemap,
} from "react-icons/fa";
import BowlingStrikeIcon from "../components/Sidebar/BowlingStrikeIcon";
import { tournamentPath } from "./tournamentUrls";

/**
 * Director work pages. BTA-111 listed Results; BTA-112 already renamed that
 * page Standings, so the sidebar keeps Standings.
 */
export const TOURNAMENT_MOBILE_PRIMARY_NAMES = [
  "Overview",
  "Scoring",
  "Standings",
];

export const isTournamentNavActive = (item, pathname) => {
  if (!item?.href) return false;
  if (item.matchPrefix) {
    return pathname.startsWith(item.matchPrefix);
  }
  if (item.isExact) {
    return pathname === item.href;
  }
  return pathname === item.href || pathname.startsWith(`${item.href}/`);
};

const upNextPagePrefix = (path) => {
  if (!path) return "";
  const scoring = path.match(/^(.*\/scoring)(?:\/|$)/);
  if (scoring) return scoring[1];
  const finance = path.match(/^(.*\/finance)(?:\/|$)/);
  if (finance) return finance[1];
  return path;
};

export const isUpNextOnCurrentPage = (pathname, link) => {
  const [path] = String(link || "").split("#");
  if (!path || !pathname) return false;
  const page = upNextPagePrefix(path);
  return pathname === page || pathname.startsWith(`${page}/`);
};

export const buildTournamentNavigation = ({
  tournamentSlug,
  location,
  signupsOn,
  dueCount = 0,
  hasBrackets = false,
  attentionCount = 0,
}) => {
  const squadOnScoring =
    location?.pathname?.includes("/scoring") &&
    new URLSearchParams(location.search || "").get("squad");

  return [
    {
      name: "Overview",
      href: tournamentPath(tournamentSlug),
      icon: FaHome,
      description: "Tournament details and information",
      isExact: true,
    },
    ...(signupsOn
      ? [
          {
            name: "Signups",
            href: tournamentPath(tournamentSlug, "/signups"),
            icon: FaUserPlus,
            description: "Online tournament signups",
            isExact: true,
          },
          ...(dueCount > 0
            ? [
                {
                  name: "Cart",
                  href: tournamentPath(tournamentSlug, "/signups/cart"),
                  icon: FaShoppingCart,
                  description: "Entries that still need payment",
                  badge: dueCount,
                  badgeClass: "bg-brand-gold text-brand-charcoal",
                },
              ]
            : []),
        ]
      : []),
    {
      name: "Scoring",
      href: tournamentPath(tournamentSlug, "/scoring/scores"),
      matchPrefix: tournamentPath(tournamentSlug, "/scoring"),
      icon: BowlingStrikeIcon,
      description: "Unified scoring interface for all squads and teams",
    },
    ...(hasBrackets
      ? [
          {
            name: "Brackets",
            href: squadOnScoring
              ? `${tournamentPath(tournamentSlug, "/brackets")}?squad=${squadOnScoring}`
              : tournamentPath(tournamentSlug, "/brackets"),
            matchPrefix: tournamentPath(tournamentSlug, "/brackets"),
            icon: FaSitemap,
            description: "Trees, tickets, and who to pay",
          },
        ]
      : []),
    {
      name: "Standings",
      href: tournamentPath(tournamentSlug, "/standings"),
      icon: FaChartBar,
      description: "Places from posted scores",
    },
    {
      name: "Payouts",
      href: tournamentPath(tournamentSlug, "/finance/payouts"),
      matchPrefix: tournamentPath(tournamentSlug, "/finance"),
      icon: FaDollarSign,
      description: "Payouts, added money, payout table, and prizes",
    },
    {
      name: "Averages",
      href: tournamentPath(tournamentSlug, "/averages"),
      icon: FaListOl,
      description: "Bowler averages and handicaps",
    },
    {
      name: "Contacts",
      href: tournamentPath(tournamentSlug, "/contacts"),
      icon: FaAddressBook,
      description: "Organization contact information",
    },
    {
      name: "Setup",
      href: tournamentPath(tournamentSlug, "/setup"),
      icon: FaCalendar,
      description: "Events, squads, signups, and documents",
      adminOnly: true,
    },
    ...(signupsOn
      ? [
          {
            name: "Signup Dashboard",
            href: tournamentPath(tournamentSlug, "/signup-dashboard"),
            mobileLabel: "Signups",
            icon: FaUserCheck,
            description:
              "Squads, waitlist, and who still owes",
            adminOnly: true,
            badge: attentionCount > 0 ? attentionCount : null,
          },
        ]
      : []),
    {
      name: "Settings",
      href: tournamentPath(tournamentSlug, "/settings"),
      icon: FaCog,
      description: "Tournament details, QR codes, and delete",
      adminOnly: true,
    },
  ];
};

export const getTournamentMobilePrimaryItems = (
  navigation,
  { canManage, signupsOn } = {}
) => {
  const items = TOURNAMENT_MOBILE_PRIMARY_NAMES.map((name) =>
    navigation.find((item) => item.name === name)
  ).filter(Boolean);

  if (signupsOn) {
    if (canManage) {
      const desk = navigation.find((item) => item.name === "Signup Dashboard");
      if (desk) return [...items, desk];
    }
    const signups = navigation.find((item) => item.name === "Signups");
    if (signups) return [...items, signups];
  }

  const payouts = navigation.find((item) => item.name === "Payouts");
  if (payouts) return [...items, payouts];
  return items;
};
